import * as state from '@/components/Analysis/state';
import {
  useGetAssemblyAttachmentsQuery,
  useGetStructureNameLazyQuery,
} from '@/__generated__/graphql';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { abyssColors } from '@/theme/colors';
import { urlToBase64 } from '@/utils';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { PictureAsPdf } from '@mui/icons-material';
import { Alert, CircularProgress, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEquipmentList, usePointOfInterestDetails, useReportSummary } from '../../hooks';
import { ReportImage } from '@/types';
import { useAssemblyData } from '@/components/Analysis/ReviewScreen/hooks/useAssemblyData';
import { useGetGenericDetailsPanels } from '@/components/Analysis/ReviewScreen/hooks/useGetGenericDetailsPanels';

// Move helper functions outside component
const generateBase64Image = async (url?: string | null) => {
  if (!url) throw new Error('Image URL is missing');
  const path = getCloudfrontUrl(url);
  if (!path) throw new Error('Failed to generate image path');
  return await urlToBase64(path);
};

const getImageDimensions = (
  base64?: string
): Promise<{ width: number; height: number }> | undefined => {
  if (!base64) return undefined;
  return new Promise((resolve) => {
    const img = new Image();
    img.addEventListener('load', () => {
      resolve({
        width: img.naturalWidth ?? 1,
        height: img.naturalHeight ?? 1,
      });
    });
    img.src = base64;
  });
};

const createPdfLink = (blob: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

const showMessage = (type: 'success' | 'error', message: string) => {
  return <Alert severity={type}>{message}</Alert>;
};

export const ExportPoiReportButton = () => {
  const selectedReport = useAtomValue(state.selectedReport);
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const summaryData = useReportSummary();
  const structureId = useGetStructureId();
  const equipmentList = useEquipmentList(selectedReport);
  const { groupPoiByEquipmentWithNumbers: groupedPOIs } = usePointOfInterestDetails();
  const [loading, setLoading] = useState(false);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();
  // Get structure name
  const [getStructureName] = useGetStructureNameLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const { data: assemblyAttachments } = useGetAssemblyAttachmentsQuery({
    variables: { assemblyId: selectedReport?.componentIds?.at(0) },
  });

  // Initialize worker
  const worker = useMemo(
    () => new Worker(new URL('generatePoiPdfWorker.tsx', import.meta.url), { type: 'module' }),
    []
  );

  useEffect(() => {
    return () => {
      worker.terminate();
    };
  }, [worker]);

  // Extract selected POI images from the report
  const selectedPointOfInterestImages = useMemo(
    () => selectedReport?.selectedPointOfInterestImages ?? [],
    [selectedReport?.selectedPointOfInterestImages]
  );

  // Fetch all selected images and prepare for PDF generation
  const fetchSelectedImages = async () => {
    const reportImages = selectedPointOfInterestImages?.flatMap((selectedPoi) =>
      selectedPoi?.selectedImages?.map((poiImage) => ({
        id: selectedPoi.pointOfInterestId,
        url: poiImage?.resourcePath,
        name: poiImage?.fileName,
        type: poiImage?.name,
      }))
    );

    const data = await (async () => {
      const fetchedImages = await Promise.allSettled(
        reportImages?.map(async (poi) => {
          const { id, url, type } = poi ?? {};
          const reportImageBase64 = await generateBase64Image(url);
          const imageDimensions = await getImageDimensions(reportImageBase64);
          return { id, base64: reportImageBase64, imageDimensions, type };
        })
      );
      return (
        fetchedImages.filter(
          (result) => result.status === 'fulfilled'
        ) as PromiseFulfilledResult<ReportImage>[]
      ).map((result) => result.value);
    })();

    return data;
  };

  const fetchSelectedAssemblyAttachments = async () => {
    const attachments = assemblyAttachments?.assembly?.files ?? [];
    const data = await Promise.allSettled(
      attachments.map(async (file) => {
        const { id, resourcePath, fileName, caption } = file ?? {};
        const base64 = await generateBase64Image(resourcePath);
        return { id, base64, type: caption ?? fileName };
      })
    );
    return (
      data.filter(
        (result) => result.status === 'fulfilled'
      ) as PromiseFulfilledResult<ReportImage>[]
    ).map((result) => result.value);
  };

  const { assemblyDetails } = useAssemblyData({
    assemblyId: selectedReport?.componentIds?.at(0) || '',
  });
  const { detailsData } = useGetGenericDetailsPanels(assemblyDetails);
  // Generate PDF with the prepared data
  const generatePdf = useCallback(
    async (structureName: string, selectedImages: ReportImage[], assemblyImages: ReportImage[]) => {
      const reportDocumentProps = {
        report: selectedReport,
        structureName,
        summaryData,
        unitSystem: unitConversionSystem,
        groupedPOIs,
        equipmentsList: equipmentList,
        allPointOfInterestTemplates,
        selectedImages,
        assemblyAttachments: assemblyImages,
        assemblyDetails: detailsData,
      };

      return new Promise((resolve, reject) => {
        const messageHandler = (event: MessageEvent) => {
          worker.removeEventListener('message', messageHandler);
          resolve(event.data);
        };

        const errorHandler = (event: ErrorEvent) => {
          worker.removeEventListener('error', errorHandler);
          reject(event.message);
        };

        worker.addEventListener('message', messageHandler);
        worker.addEventListener('error', errorHandler);
        worker.postMessage({ reportDocumentProps });
      });
    },
    [
      selectedReport,
      summaryData,
      unitConversionSystem,
      groupedPOIs,
      equipmentList,
      allPointOfInterestTemplates,
      detailsData,
      worker,
    ]
  );

  // Main handler for the PDF download button
  const handleDownloadPDF = async () => {
    if (loading) return;

    try {
      setLoading(true);
      let structureName = '';

      // Ensure we have the structure name
      if (structureId) {
        const { data } = await getStructureName({ variables: { structureId } });
        structureName = data?.structure?.name ?? '';
      }

      // Fetch images
      const selectedImages = await fetchSelectedImages();
      const assemblyImages = await fetchSelectedAssemblyAttachments();
      // Generate PDF
      const pdfBlob = (await generatePdf(structureName, selectedImages, assemblyImages)) as Blob;

      // Download PDF
      createPdfLink(pdfBlob, `${selectedReport?.name ?? 'report'}.pdf`);

      // Show success message
      setSnackbarMessage({
        shouldShow: true,
        content: showMessage('success', 'PDF generated successfully'),
      });
    } catch (error) {
      // Show error message
      setSnackbarMessage({
        shouldShow: true,
        content: showMessage(
          'error',
          (error as string).split(':')[1]?.trim() || 'Failed to generate PDF'
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <CircularProgress
          sx={{ color: abyssColors.primary[500] }}
          size={16}
          data-testId="generate-pdf-loading"
        />
      ) : (
        <Tooltip title="Please stay on this page until the PDF generates">
          <PictureAsPdf
            data-testid="export-report-icon"
            htmlColor={abyssColors.primary[500]}
            sx={{ cursor: 'pointer' }}
            onClick={handleDownloadPDF}
          />
        </Tooltip>
      )}
    </>
  );
};
